/* eslint-disable react/button-has-type */
/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Oval } from 'react-loader-spinner';
import fileDownload from 'js-file-download';
import axios from 'axios';

import { ReactComponent as CloseIcon } from '@/assets/icons/close.icon.svg';
import { ReactComponent as DownIcon } from '@/assets/icons/nav-arrow-down.icon.svg';
import { ReactComponent as WarnIcon } from '@/assets/icons/warn.icon.svg';
import { ReactComponent as RefreshIcon } from '@/assets/icons/refresh.icon.svg';
import { ReactComponent as DownloadIcon } from '@/assets/icons/download.icon.svg';
import useOnClickOutside from '@/hooks/useClickOutside';
import { numberWithCommas } from '@/utils/helpers';
import PDFViewer from '@/components/PDFViewer';
import ModalLoader from '@/components/ModalLoader';
import {
  approveInvoice,
  deleteInvoice,
  duplicateInvoice,
  markAsSent
} from '@/services/receivablesApi';
import MarkInvoiceAsPaid from './MarkInvoiceAsPaid';
import PromptModal from '@/components/PromptModal';
import { fetchUser } from '@/services/userApi';
import AddBankAccountModal from '@/pages/Settings/components/AddBankAccountModal';
import { device } from '@/constants/breakpoints';
import { fetchInvoicePaymentPDF } from '@/services/billsApi';

function InvoiceDetails({ showModal, setShowModal, invoice, setShowEditModal }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showActions, setShowActions] = useState(false);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteTypeModal, setShowDeleteTypeModal] = useState(false);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [showMarkAsPaidModal, setShowMarkAsPaidModal] = useState(false);
  const [loadingPaymentAdvice, setLoadingPaymentAdvice] = useState(false);
  const [saveAs, setSaveAs] = useState('pdf');
  const [showShareOptions, setShowShareOptions] = useState(false);
  const shareRef = useRef();
  const [deleteType, setDeleteType] = useState('single');
  const ref = useRef();
  useOnClickOutside(ref, () => setShowActions(false));
  useOnClickOutside(shareRef, () => setShowShareOptions(false));

  const fetchPaymentAdvice = useQuery({
    queryKey: ['paymentAdvice', invoice?.billOrInvoiceNumber],
    queryFn: fetchInvoicePaymentPDF,
    enabled: invoice?.status === 'paid'
  });

  const handleFileDownload2 = (url, filename) => {
    setLoadingPaymentAdvice(true);
    const parts = filename.split('/');
    const newFileName = parts[parts.length - 1];
    axios
      .get(url, {
        responseType: 'blob'
      })
      .then((res) => {
        setLoadingPaymentAdvice(false);
        fileDownload(res.data, newFileName);
      })
      .catch(() => {
        setLoadingPaymentAdvice(false);
      });
  };

  const user = useQuery({
    queryKey: ['user'],
    queryFn: fetchUser,
    onSuccess: () => { },
    onError: () => { }
  });

  const handleFileDownload = (url, filename) => {
    const parts = filename.split('/');
    const newFileName = parts[parts.length - 1];
    axios
      .get(url, {
        responseType: 'blob'
      })
      .then((res) => {
        fileDownload(res.data, newFileName);
      });
  };

  const handleApproveInvoice = useMutation(({ id, data }) => approveInvoice(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoicesMetrics'] });
      navigate('/get-paid/invoices?tab=approved');
      toast.success('Invoice approved successfully');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    }
  });

  const handleMarkAsSent = useMutation(markAsSent, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoicesMetrics'] });
      navigate('/get-paid/invoices?tab=sent');
      toast.success('Invoice updated successfully');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    }
  });

  const handleDeleteInvoice = useMutation(({ id, type }) => deleteInvoice(id, type), {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoicesMetrics'] });
      toast.success('Invoice deleted successfully');
      setShowDeleteModal(false);
      setShowDeleteTypeModal(false);
      setShowModal(false);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    }
  });

  const handleDuplicateInvoice = useMutation(({ id }) => duplicateInvoice(id), {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoicesMetrics'] });
      toast.success('Invoice duplicated successfully');
      navigate('/get-paid/invoices?tab=draft');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    }
  });

  return (
    <AnimatePresence>
      {showModal && (
        <ModalView
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="modal-overlay">
          <motion.div
            initial={{ y: 1000 }}
            animate={{ y: 0 }}
            exit={{ y: 100 }}
            transition={{ duration: 0.3 }}
            className="modal"
            onClick={(e) => e.stopPropagation()}>
            {/* ======== Content ======== */}
            {fetchPaymentAdvice.isFetching ? (
              <LoaderView>
                <Oval color="#92c22c" height={50} width={50} />
              </LoaderView>
            ) : (
              <div className="modal-content">
                <div className="header">
                  <span></span>
                  <button type="button" onClick={() => setShowModal(false)}>
                    <CloseIcon />
                  </button>
                </div>
                <div className="modal-content__title-view">
                  <h3>Invoice Details</h3>
                </div>
                <div className="modal-content__values">
                  <div className="modal-content__values-item">
                    <span>Customer Name</span>
                    <h3>{invoice?.customer?.companyName}</h3>
                  </div>
                  <div className="modal-content__values-item">
                    <span>Amount</span>
                    <h3>₦{numberWithCommas(invoice?.amount || '00.00')}</h3>
                  </div>
                  <div className="modal-content__values-item">
                    <span>Due Date</span>
                    <h3>{format(new Date(invoice?.dueDate || new Date()), 'do MMM, yyyy')}</h3>
                  </div>
                  <div className="modal-content__values-item">
                    <span>Invoice no.</span>
                    <h3>{invoice?.billOrInvoiceNumber}</h3>
                  </div>
                </div>
                <div className="modal-content__row">
                  <div className="file-view">
                    {invoice?.fileUrl?.includes('.pdf') ? (
                      <div className="pdf-view">
                        <PDFViewer file={invoice?.fileUrl} />
                      </div>
                    ) : (
                      <div className="image-view">
                        {invoice?.fileUrl ? (
                          <img src={invoice?.fileUrl} alt="Invoice" />
                        ) : invoice?.items?.length > 0 ? (
                          <div className="refresh">
                            <p>Please hold on, generating invoice...</p>
                            <button
                              onClick={() => {
                                // refetch();
                              }}>
                              <RefreshIcon /> Refresh
                            </button>
                          </div>
                        ) : (
                          <p>No File to display</p>
                        )}
                      </div>
                    )}

                    {invoice?.fileUrl && (
                      <div className="download-view">
                        <button
                          onClick={() => {
                            handleFileDownload(invoice?.fileUrl, invoice?.fileUrl);
                          }}>
                          Download Invoice <DownloadIcon />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="action-view">
                    <div className="balance">
                      <span>Balance due</span>
                      <h3>₦{numberWithCommas(invoice?.balance || '00.00')}</h3>
                    </div>

                    <div className="action">
                      <button
                        type="button"
                        onClick={(e) => {
                          setShowActions(true);
                          e.stopPropagation();
                        }}>
                        Action <DownIcon />
                      </button>
                      <AnimatePresence>
                        {showActions ? (
                          <motion.div
                            className="menu-actions"
                            ref={ref}
                            initial={{ y: -20 }}
                            animate={{ y: 0 }}
                            exit={{ y: -10, opacity: 0 }}
                            transition={{ duration: 0.2 }}>
                            {invoice?.status === 'sent' && (
                              <>
                                {/* {invoice?.items?.length > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigate(`/get-paid/invoices/${invoice?.id}`);
                                  }}>
                                  Edit invoice
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    setShowEditModal(true);
                                    e.stopPropagation();
                                  }}>
                                  Edit invoice
                                </button>
                              )} */}
                                {(!invoice?.requiresApproval || !invoice?.connectionId) && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setShowMarkAsPaidModal(true);
                                    }}>
                                    Mark as paid
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() =>
                                    navigate(
                                      `/get-paid/invoices/request-approval?id=${invoice?.id}`
                                    )
                                  }>
                                  Resend invoice
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleDuplicateInvoice.mutate({
                                      id: invoice?.id
                                    });
                                  }}>
                                  Duplicate invoice
                                </button>
                                {invoice?.isOrigin && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setShowDeleteTypeModal(true);
                                    }}>
                                    Delete invoice
                                  </button>
                                )}
                                <button type="button" onClick={() => setShowModal(false)}>
                                  Cancel
                                </button>
                              </>
                            )}
                            {invoice?.status === 'partially-paid' && (
                              <>
                                <button
                                  type="button"
                                  onClick={() =>
                                    navigate(
                                      `/get-paid/invoices/request-payment-type/${invoice?.id}`
                                    )
                                  }>
                                  Request payment
                                </button>
                                {(!invoice?.requiresApproval || !invoice?.connectionId) && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setShowMarkAsPaidModal(true);
                                    }}>
                                    Mark as paid
                                  </button>
                                )}

                                <button
                                  type="button"
                                  onClick={() => {
                                    handleDuplicateInvoice.mutate({
                                      id: invoice?.id
                                    });
                                  }}>
                                  Duplicate invoice
                                </button>
                                <button type="button" onClick={() => setShowModal(false)}>
                                  Cancel
                                </button>
                              </>
                            )}
                            {invoice?.status === 'paid' && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleDuplicateInvoice.mutate({
                                      id: invoice?.id
                                    });
                                  }}>
                                  Duplicate invoice
                                </button>
                                <button type="button" onClick={() => setShowModal(false)}>
                                  Cancel
                                </button>
                              </>
                            )}
                            {invoice?.status === 'approved' && (
                              <>
                                <button
                                  onClick={() => {
                                    navigate(
                                      `/get-paid/invoices/request-payment-type/${invoice?.id}`
                                    );
                                  }}>
                                  Request payment
                                </button>
                                {(!invoice?.requiresApproval || !invoice?.connectionId) && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setShowMarkAsPaidModal(true);
                                    }}>
                                    Mark as paid
                                  </button>
                                )}

                                <button
                                  type="button"
                                  onClick={() => {
                                    handleDuplicateInvoice.mutate({
                                      id: invoice?.id
                                    });
                                  }}>
                                  Duplicate invoice
                                </button>
                                {invoice?.isOrigin && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setShowDeleteTypeModal(true);
                                    }}>
                                    Delete invoice
                                  </button>
                                )}
                              </>
                            )}
                            {invoice?.status === 'draft' && (
                              <>
                                {invoice?.items?.length > 0 ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      navigate(`/get-paid/invoices/${invoice?.id}`);
                                    }}>
                                    Edit invoice
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      setShowEditModal(true);
                                      e.stopPropagation();
                                    }}>
                                    Edit invoice
                                  </button>
                                )}
                                {invoice?.requiresApproval ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      navigate(
                                        `/get-paid/invoices/request-approval?id=${invoice?.id}`
                                      );
                                    }}>
                                    Send invoice
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      navigate(
                                        `/get-paid/invoices/request-payment?id=${invoice?.id}`
                                      );
                                    }}>
                                    Send invoice
                                  </button>
                                )}

                                <button
                                  type="button"
                                  onClick={() => {
                                    handleMarkAsSent.mutate(invoice?.id);
                                  }}>
                                  Mark as sent
                                </button>

                                <button
                                  type="button"
                                  onClick={() => {
                                    handleDuplicateInvoice.mutate({
                                      id: invoice?.id
                                    });
                                  }}>
                                  Duplicate invoice
                                </button>

                                {invoice?.isOrigin && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setShowDeleteModal(true);
                                    }}>
                                    Delete invoice
                                  </button>
                                )}
                                <button type="button" onClick={() => setShowModal(false)}>
                                  Cancel
                                </button>
                              </>
                            )}
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                    </div>

                    {invoice?.status === 'draft' && (
                      <div className="request">
                        {invoice?.requiresApproval ? (
                          <button
                            type="button"
                            onClick={() => {
                              navigate(`/get-paid/invoices/request-approval?id=${invoice?.id}`);
                            }}>
                            Send invoice
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              if (user?.data?.data?.hasBankAccount) {
                                navigate(`/get-paid/invoices/request-payment?id=${invoice?.id}`);
                              } else {
                                setShowPromptModal(true);
                              }
                            }}>
                            Send invoice
                          </button>
                        )}
                      </div>
                    )}
                    {invoice?.status === 'approved' && (
                      <div className="request">
                        <button
                          onClick={() => {
                            navigate(`/get-paid/invoices/request-payment-type/${invoice?.id}`);
                          }}>
                          Request payment
                        </button>
                      </div>
                    )}
                    {invoice?.status === 'partially-paid' && (
                      <div className="request">
                        <button
                          onClick={() => {
                            navigate(`/get-paid/invoices/request-payment-type/${invoice?.id}`);
                          }}>
                          Request payment
                        </button>
                      </div>
                    )}

                    {invoice?.status === 'sent' && (
                      <div className="request">
                        <button
                          onClick={() => {
                            navigate(`/get-paid/invoices/request-approval?id=${invoice?.id}`);
                          }}>
                          Resend invoice
                        </button>
                      </div>
                    )}

                    {invoice?.status === 'paid' && (
                      <div className="request">
                        <button
                          type="button"
                          className="request-btn"
                          onClick={() => setShowShareOptions(true)}>
                          Download Receipt
                        </button>

                        <AnimatePresence>
                          {showShareOptions && (
                            <motion.div
                              className="menu-actionss"
                              ref={shareRef}
                              initial={{ y: 20 }}
                              animate={{ y: 0 }}
                              exit={{ y: -20, opacity: 0 }}
                              transition={{ duration: 0.2 }}>
                              <button
                                className="action"
                                onClick={() => {
                                  setSaveAs('png');
                                  handleFileDownload2(
                                    fetchPaymentAdvice?.data?.data?.url.replace('.pdf', '.png'),
                                    `payment-advice-${invoice?.billOrInvoiceNumber}.png`
                                  );
                                }}>
                                {loadingPaymentAdvice && saveAs === 'png'
                                  ? 'Downloading...'
                                  : ' Download Image'}
                              </button>
                              <button
                                className="action"
                                onClick={() => {
                                  setSaveAs('pdf');
                                  handleFileDownload2(
                                    fetchPaymentAdvice?.data?.data?.url,
                                    `payment-advice-${invoice?.billOrInvoiceNumber}.pdf`
                                  );
                                }}>
                                {loadingPaymentAdvice && saveAs === 'pdf'
                                  ? 'Downloading...'
                                  : ' Download PDF'}
                              </button>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </ModalView>
      )}

      <MarkInvoiceAsPaid
        showModal={showMarkAsPaidModal}
        setShowModal={setShowMarkAsPaidModal}
        bill={invoice}
      />
      {handleApproveInvoice.isLoading && <ModalLoader message="Approving invoice..." />}
      {handleMarkAsSent.isLoading && <ModalLoader message="Updating invoice..." />}
      {handleDuplicateInvoice.isLoading && <ModalLoader message="Duplicating invoice..." />}

      <PromptModal showModal={showPromptModal}>
        <div className="header">
          <button onClick={() => setShowPromptModal(false)}>
            <CloseIcon />
          </button>
        </div>
        <div className="content">
          <WarnIcon className="icon" />
          <h3>Add bank account</h3>
          <p>You need to add a bank account to request payment. Click the button below to add</p>
          <button
            className="continue"
            onClick={() => {
              setShowPromptModal(false);
              setShowAddAccountModal(true);
            }}>
            Continue
          </button>
        </div>
      </PromptModal>

      {/* Delete type */}
      <PromptModal showModal={showDeleteTypeModal}>
        <div className="header">
          <button type="button" onClick={() => setShowDeleteTypeModal(false)}>
            <CloseIcon />
          </button>
        </div>
        <div className="content">
          <div className="question-icon">
            <span>?</span>
          </div>
          <h3>Select an option</h3>
          <div className="row">
            <button
              type="button"
              className="primary"
              onClick={() => {
                setDeleteType('single');
                setShowDeleteModal(true);
              }}>
              Delete for me
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() => {
                setDeleteType('all');
                setShowDeleteModal(true);
              }}>
              Delete for everyone
            </button>
          </div>
        </div>
      </PromptModal>

      <PromptModal showModal={showDeleteModal}>
        <div className="header">
          <button type="button" onClick={() => setShowDeleteModal(false)}>
            <CloseIcon />
          </button>
        </div>
        <div className="content">
          <WarnIcon />
          <h3>Delete invoice</h3>
          <p>
            Are you sure you want to delete this invoice? When you delete an invoice, it will be
            removed from the list and placed in the archive.
          </p>
          <button
            type="button"
            className="delete"
            onClick={() =>
              handleDeleteInvoice.mutate({
                id: invoice?.id,
                type: deleteType
              })
            }>
            {handleDeleteInvoice.isLoading ? (
              <Oval color="#fff" secondaryColor="#ddd" width={30} height={30} strokeWidth={4} />
            ) : (
              'Delete'
            )}
          </button>
        </div>
      </PromptModal>

      <AddBankAccountModal
        showModal={showAddAccountModal}
        setShowModal={setShowAddAccountModal}
        shouldNavigate
        url={`/get-paid/invoices/request-payment?id=${invoice?.id}`}
      />
    </AnimatePresence>
  );
}

export default InvoiceDetails;

const ModalView = styled(motion.div)`
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  z-index: 9009;
  display: flex;
  align-items: center;
  justify-content: center;

  .modal {
    background-color: ${(props) => props.theme.colors?.white};
    z-index: 30000;
    position: fixed;
    width: 60%;
    padding: 30px 0px;
    padding-top: 0;
    padding-bottom: 20px;
    border-radius: 16px;
    max-height: 90%;
    overflow-y: scroll;

    @media ${device.phone} {
      width: calc(100% - 32px);
      border-radius: 16px;
    }

    .modal-content {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: flex-start;
      margin: 0;
      justify-content: flex-start;
      flex-direction: column;
      padding: 30px;

      .header {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;

        @media ${device.phone} {
          width: 100%;
        }
      }

      .modal-content__title-view {
        width: 100%;
        margin-top: 20px;

        h3 {
          font-size: 1.25rem;
          font-weight: 600;
          color: ${(props) => props.theme.colors?.secondary};
        }
      }

      .modal-content__values {
        width: 100%;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        margin-top: 30px;

        @media ${device.phone} {
          width: 100%;
          flex-direction: column;
        }

        .modal-content__values-item {
          width: 100%;

          @media ${device.phone} {
            width: 100%;
            margin-bottom: 20px;
          }

          span {
            font-size: 0.875rem;
            font-weight: 400;
            color: ${(props) => props.theme.colors?.secondary};
          }

          h3 {
            font-size: 1.25rem;
            font-weight: 600;
            color: ${(props) => props.theme.colors?.secondary};
            margin-top: 8px;
          }
        }
      }

      .modal-content__row {
        width: 100%;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        margin-top: 30px;

        @media ${device.phone} {
          width: 100%;
          flex-direction: column-reverse;
        }

        .file-view {
          width: 55%;

          .download-view {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            margin-top: 20px;

            button {
              width: 180px;
              height: 50px;
              display: flex;
              align-items: center;
              justify-content: flex-end;
              background: transparent;
              border-radius: 8px;
              border: none;
              color: ${(props) => props.theme.colors?.secondary};
              font-weight: 500;
            }
          }

          .pdf-view {
            width: 100%;
            height: 450px;
            border: 1px solid #e0e0e0;
            overflow: scroll;

            @media ${device.phone} {
              width: 100%;
              height: 400px;
            }

            .react-pdf__Page {
              max-width: calc(100% - 1em);
              box-shadow: 0 0 8px rgba(0, 0, 0, 0.2);
              margin: 1em;
            }

            .react-pdf__Page canvas {
              max-width: 100%;
              height: auto !important;
            }

            .react-pdf__message {
              padding: 1em;
              color: white;
            }
          }

          .image-view {
            width: 100%;
            height: 450px;
            border: 1px solid #e0e0e0;

            @media ${device.phone} {
              width: 100%;
              height: 400px;
            }

            img {
              width: 100%;
              height: 100%;
              object-fit: contain;
            }

            p {
              padding: 20px;
              text-align: center;
              color: ${(props) => props.theme.colors?.secondary};
            }

            .refresh {
              width: 100%;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;

              p {
                padding: 20px;
                text-align: center;
                color: ${(props) => props.theme.colors?.secondary};
              }

              button {
                color: ${(props) => props.theme.colors?.secondary};
                display: flex;
                align-items: center;
                justify-content: center;

                svg {
                  margin-right: 5px;

                  path {
                    stroke: ${(props) => props.theme.colors?.secondary};
                  }
                }
              }
            }
          }
        }

        .action-view {
          width: 40%;
          height: 450px;
          position: relative;

          @media ${device.phone} {
            width: 100%;
            height: auto;
            margin-bottom: 20px;
          }

          .balance {
            width: 100%;

            span {
              font-size: 0.875rem;
              font-weight: 400;
              color: ${(props) => props.theme.colors?.secondary};
            }

            h3 {
              font-size: 1.25rem;
              font-weight: 600;
              color: ${(props) => props.theme.colors?.secondary};
              margin-top: 8px;
            }
          }

          .action {
            width: 100%;
            margin-top: 30px;
            position: relative;

            button {
              width: 120px;
              height: 40px;
              background: ${(props) => props.theme.colors?.primary};
              border-radius: 8px;
              border: none;
              color: ${(props) => props.theme.colors?.white};
              font-size: 0.875rem;
              font-weight: 600;
              display: flex;
              align-items: center;
              justify-content: center;

              svg {
                margin-left: 10px;

                path {
                  stroke: ${(props) => props.theme.colors?.white};
                }
              }
            }

            .menu-actions {
              position: absolute;
              top: 45px;
              left: 0;
              background-color: ${(props) => props.theme.colors?.white};
              border-radius: 6px;
              box-shadow: 0px 11.3975px 34.1925px rgba(0, 0, 0, 0.08);
              width: 180px;
              z-index: 100;
              overflow: hidden;

              button {
                width: 100%;
                padding: 16px 24px;
                border: none;
                background-color: ${(props) => props.theme.colors?.white};
                font-size: 0.875rem;
                color: ${(props) => props.theme.colors?.secondary};
                font-weight: 400;
                display: flex;
                align-items: center;
                justify-content: flex-start;

                &:hover {
                  background-color: ${(props) => props.theme.colors?.layer2};
                }
              }
            }
          }

          .request {
            width: 100%;
            position: absolute;
            bottom: 0;
            display: flex;
            align-items: center;
            justify-content: space-between;

            @media ${device.phone} {
              width: 100%;
            }

            button {
              width: 180px;
              height: 50px;
              background: ${(props) => props.theme.colors?.white};
              border: 1px solid ${(props) => props.theme.colors?.primary};
              border-radius: 8px;
              color: ${(props) => props.theme.colors?.primary};
              font-size: 0.875rem;
              font-weight: 600;
              display: flex;
              align-items: center;
              justify-content: center;

              @media ${device.phone} {
                width: 48%;
              }
            }

            .download {
              width: 180px;
              height: 50px;
              background: ${(props) => props.theme.colors?.white};
              border: 1px solid ${(props) => props.theme.colors?.primary};
              border-radius: 8px;
              color: ${(props) => props.theme.colors?.primary};
              font-size: 0.875rem;
              font-weight: 600;
              display: flex;
              align-items: center;
              justify-content: center;

              @media ${device.phone} {
                width: 48%;
                font-size: 0.8rem;
              }
            }

            .request-btn {
              width: 180px;
              height: 50px;
              display: flex;
              align-items: center;
              justify-content: center;
              background: ${(props) => props.theme.colors?.primary};
              border-radius: 8px;
              color: ${(props) => props.theme.colors?.white};
              font-weight: 500;

              @media ${device.phone} {
                width: 48%;
              }
            }

            .menu-actionss {
              position: absolute;
              top: -80px;
              left: 0;
              background-color: ${(props) => props.theme.colors?.white};
              border-radius: 6px;
              box-shadow: 0px 11.3975px 34.1925px rgba(0, 0, 0, 0.08);
              width: 180px;
              z-index: 100;
              overflow: hidden;
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              justify-content: flex-start;

              .action {
                width: 100%;
                padding: 0 20px;
                margin: 0;
                display: flex;
                align-items: center;
                justify-content: flex-start;
                border-radius: 0;
                height: 40px;
                background-color: ${(props) => props.theme.colors?.white};
                border: none;
                color: ${(props) => props.theme.colors?.secondary};
                font-weight: 500;

                &:hover {
                  background-color: ${(props) => props.theme.colors?.layer};
                }
              }
            }
          }
        }
      }
    }
  }
`;

const LoaderView = styled.div`
  width: 100%;
  min-height: 300px;
  display: flex;
  align-items: center;
  margin: 0;
  justify-content: center;
`;
